import React from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  alpha,
} from '@mui/material';
import StoreIcon from '@mui/icons-material/Store';
import type { Location } from '../../../types';

interface POSColors {
  bg: string;
  surface: string;
  surfaceLight: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  accent: string;
  accentLight: string;
}

interface LocationSelectorProps {
  stores: Location[];
  activeStoreId: string | null;
  onLocationChange: (storeId: string) => void;
  colors: POSColors;
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
  stores,
  activeStoreId,
  onLocationChange,
  colors,
}) => {
  // Don't render if no stores
  if (!stores || stores.length === 0) {
    return null;
  }

  const currentStore = stores.find((s) => s.id === activeStoreId);
  const hasMultipleStores = stores.length > 1;

  // Single store: show static display
  if (!hasMultipleStores) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.5,
        }}
      >
        <StoreIcon
          data-testid="StoreIcon"
          sx={{
            fontSize: 18,
            color: colors.accent,
          }}
        />
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: colors.textPrimary,
          }}
        >
          {currentStore?.name || stores[0]?.name}
        </Typography>
      </Box>
    );
  }

  // Multiple stores: show dropdown
  return (
    <FormControl
      size="small"
      fullWidth
      sx={{
        minWidth: 150,
        maxWidth: 220,
      }}
    >
      <InputLabel
        id="location-selector-label"
        sx={{
          color: colors.textMuted,
          '&.Mui-focused': {
            color: colors.accent,
          },
        }}
      >
        Location
      </InputLabel>
      <Select
        labelId="location-selector-label"
        id="location-selector"
        value={activeStoreId || ''}
        label="Location"
        onChange={(e) => onLocationChange(e.target.value as string)}
        startAdornment={
          <StoreIcon
            data-testid="StoreIcon"
            sx={{
              fontSize: 18,
              color: colors.accent,
              mr: 1,
            }}
          />
        }
        sx={{
          backgroundColor: colors.surface,
          borderRadius: '10px',
          fontSize: '13px',
          fontWeight: 600,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.textMuted,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colors.accent,
            borderWidth: 2,
          },
          '& .MuiSelect-select': {
            color: colors.textPrimary,
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiSvgIcon-root': {
            color: colors.textMuted,
          },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: '10px',
              mt: 0.5,
              '& .MuiMenuItem-root': {
                fontSize: '13px',
                fontWeight: 500,
                color: colors.textPrimary,
                py: 1.5,
                '&:hover': {
                  backgroundColor: alpha(colors.accent, 0.1),
                },
                '&.Mui-selected': {
                  backgroundColor: alpha(colors.accent, 0.15),
                  '&:hover': {
                    backgroundColor: alpha(colors.accent, 0.2),
                  },
                },
              },
            },
          },
        }}
      >
        {stores.map((store) => (
          <MenuItem key={store.id} value={store.id}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                {store.name}
              </Typography>
              {store.city && (
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: colors.textMuted,
                    ml: 0.5,
                  }}
                >
                  ({store.city})
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LocationSelector;
